"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { StoreLink, useStoreHref } from "@/components/store";
import { useTranslations } from "next-intl";
import type { NavigationItem } from "@cobrastyle/shared-types";
import {
  getSearchSuggestions,
  type SearchSuggestionsResult,
} from "@cobrastyle/storefront-core/actions";
import { buttonClassName, Spinner } from "@cobrastyle/ui";
import { PriceLabel } from "@/components/ui/price-label";
import { cn } from "@cobrastyle/ui";
import type { BrandMenu } from "./navigation";
import { searchBrands, type BrandSuggestion } from "@/actions/brand-search";
import { fetchProductBrands } from "@/actions/product-brand";
import type { PopularSearchTerm } from "@/lib/popular-search-terms";
import { SearchContactText } from "@/components/search/search-contact-text";
import { useRecentSearches } from "@/lib/use-recent-searches";
import { MIN_QUERY_LENGTH } from "@/lib/search-constants";

// Helper function to highlight matching text
function HighlightedText({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;

  const regex = new RegExp(
    `(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi",
  );
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <strong key={i} className="font-bold text-foreground">
            {part}
          </strong>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

interface SearchBarProps {
  className?: string;
  autoFocus?: boolean;
  onSearch?: () => void;
  /** Top-level categories shown as a discovery column before the user types anything. */
  categories?: NavigationItem[];
  /** Curated brands, reusing the same payload the header's mega menu already fetches. */
  brandMenu?: BrandMenu | null;
  /** Merchant-moderated popular search terms, fetched once in header.tsx. */
  popularSearchTerms?: PopularSearchTerm[];
}

export function SearchBar({
  className,
  autoFocus = false,
  onSearch,
  categories = [],
  brandMenu,
  popularSearchTerms = [],
}: SearchBarProps) {
  const t = useTranslations("common");
  const tSearch = useTranslations("search");
  const tBrands = useTranslations("brands");
  const tProduct = useTranslations("product");
  const [query, setQuery] = useState("");
  // The query suggestions/brandMatches actually answer — updated only once new
  // content is ready (or immediately for a short query, which needs no fetch),
  // never on every keystroke. Everything that decides *what panel to show*
  // reads this instead of the live `query`, so the panel keeps showing its
  // last content through the debounce+fetch window instead of going blank.
  const [committedQuery, setCommittedQuery] = useState("");
  const [suggestions, setSuggestions] =
    useState<SearchSuggestionsResult | null>(null);
  const [brandMatches, setBrandMatches] = useState<BrandSuggestion[]>([]);
  // SKU -> th_fabrikat label, for the products shown below. A second fetch
  // because it needs the SKUs getSearchSuggestions returns first — see
  // fetchSuggestions below.
  const [productBrands, setProductBrands] = useState<Record<string, string>>({});
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const { recentSearches, saveRecentSearch, clearRecentSearch, clearAllRecentSearches } =
    useRecentSearches();
  // Viewport y where the full-width dropdown starts — measured from the input's
  // bottom so it aligns under the (variable-height) sticky header, not a fixed offset.
  const [dropdownTop, setDropdownTop] = useState(112);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // The dropdown is portaled to <body>, so it's outside containerRef — track it
  // separately so click-outside doesn't treat clicks inside it (e.g. a product) as outside.
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const router = useRouter();
  const storePath = useStoreHref();

  // Fetch suggestions with debounce
  const fetchSuggestions = useCallback(async (searchQuery: string) => {
    if (searchQuery.trim().length < MIN_QUERY_LENGTH) {
      // No fetch needed, so this can commit immediately — the discovery
      // panel it switches to is always available, nothing to wait on.
      setCommittedQuery(searchQuery);
      setSuggestions(null);
      setBrandMatches([]);
      setProductBrands({});
      return;
    }

    setIsLoading(true);
    try {
      // All three run together: brand matches and product brands come from
      // separate actions because neither is on the shared package's queries
      // (brand matches need the full ~73-brand catalogue, not just the
      // curated "main brands" the client holds — see header.tsx; th_fabrikat
      // is a Torsbo-only attribute). Both are keyed by the same search term
      // as getSearchSuggestions, not by its result, so none of the three
      // waits on another.
      const [result, brands, brandsBySku] = await Promise.all([
        getSearchSuggestions(searchQuery),
        searchBrands(searchQuery),
        fetchProductBrands(searchQuery),
      ]);
      setSuggestions(result);
      setBrandMatches(brands);
      setProductBrands(brandsBySku);
      setCommittedQuery(searchQuery);
      setIsOpen(true);
    } catch (error) {
      // Deliberately leave committedQuery/suggestions/brandMatches/productBrands
      // untouched: a failed fetch never becomes "ready", so keep showing the
      // last good panel rather than flashing to empty.
      console.error("Failed to fetch suggestions:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle input change with debounce
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedIndex(-1);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  // Handle form submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      saveRecentSearch(query.trim());
      router.push(storePath(`/sok?q=${encodeURIComponent(query.trim())}`));
      setIsOpen(false);
      onSearch?.();
    }
  };

  // Handle recent search click
  const handleRecentSearchClick = (term: string) => {
    setQuery(term);
    saveRecentSearch(term);
    router.push(storePath(`/sok?q=${encodeURIComponent(term)}`));
    setIsOpen(false);
    onSearch?.();
  };

  // Handle popular search term click. `redirect` is Magento's own configured
  // destination for that term (set by an admin) — when present, go straight
  // there instead of running a search for the term's text.
  const handlePopularTermClick = (term: PopularSearchTerm) => {
    setIsOpen(false);
    onSearch?.();
    if (term.redirect) {
      const path = term.redirect.startsWith("/") ? term.redirect : `/${term.redirect}`;
      router.push(storePath(path));
      return;
    }
    setQuery(term.text);
    saveRecentSearch(term.text);
    router.push(storePath(`/sok?q=${encodeURIComponent(term.text)}`));
  };

  // Handle product click
  const handleProductClick = () => {
    if (query.trim()) {
      saveRecentSearch(query.trim());
    }
    setIsOpen(false);
    onSearch?.();
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const totalItems =
      (suggestions?.categories.length ?? 0) +
      (suggestions?.products.length ?? 0);

    if (!isOpen || totalItems === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % totalItems);
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + totalItems) % totalItems);
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0) {
          const categoriesCount = suggestions?.categories.length ?? 0;
          if (selectedIndex < categoriesCount) {
            const cat = suggestions!.categories[selectedIndex];
            router.push(storePath(`/${cat.urlPath}`));
            setIsOpen(false);
            onSearch?.();
          } else {
            const productIndex = selectedIndex - categoriesCount;
            const product = suggestions!.products[productIndex];
            router.push(storePath(`/${product.urlKey}`));
            setIsOpen(false);
            onSearch?.();
          }
        } else {
          handleSubmit(e);
        }
        break;
      case "Escape":
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inInput = containerRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      if (!inInput && !inDropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Anchor the full-width dropdown just under the search input (measured, so it
  // stays aligned regardless of header height — USP strip, breakpoint changes).
  useEffect(() => {
    if (!isOpen) return;
    const update = () => {
      if (containerRef.current) {
        setDropdownTop(containerRef.current.getBoundingClientRect().bottom + 10);
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [isOpen]);

  // Defaulted here (rather than guarding every JSX reference below) since
  // brandMatches alone can make hasSuggestions true while suggestions is still null.
  const suggestedCategories = suggestions?.categories ?? [];
  const suggestedProducts = suggestions?.products ?? [];
  const hasSuggestions =
    suggestedCategories.length > 0 ||
    suggestedProducts.length > 0 ||
    brandMatches.length > 0;
  // Both based on committedQuery, not the live query, so the panel's content
  // only changes when there's something new to show it — see committedQuery.
  const showInitialState =
    !committedQuery.trim() || committedQuery.trim().length < MIN_QUERY_LENGTH;
  // No `!isLoading` guard: `suggestions` is only ever set once a fetch
  // resolves, so a truthy value here already means "a completed answer for
  // committedQuery" — gating on isLoading too would hide that stale-but-valid
  // answer the moment a newer fetch starts, recreating the blank-flash gap.
  const noResults =
    committedQuery.trim().length >= MIN_QUERY_LENGTH &&
    suggestions &&
    !hasSuggestions;
  // The empty state (no query yet) has four independent sources — history,
  // categories, brands, popular terms — any one of which is enough to make
  // the panel worth opening.
  const hasDiscoveryContent =
    recentSearches.length > 0 ||
    categories.length > 0 ||
    (brandMenu?.brands.length ?? 0) > 0 ||
    popularSearchTerms.length > 0;
  const hasContent =
    hasSuggestions || noResults || (showInitialState && hasDiscoveryContent);

  return (
    // z-50 keeps the input above the full-page backdrop below (z-40) so it's
    // the one thing besides the results panel left sharp when the overlay is open.
    <div ref={containerRef} className={cn("relative z-50", className)}>
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative flex items-center">
          {/* Search mark. The canvas drew this as a bare circle, but the mobile
              header's own search field carries a magnifier — and a bare circle
              beside a magnifier reads as two different controls, not one search
              box at two widths. Same path, size and weight as
              mobile-search-trigger; only the 16px inset is the canvas's. */}
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute left-4 h-4 w-4 flex-none text-muted"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>

          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsOpen(true)}
            placeholder={tSearch("headerPlaceholder")}
            autoFocus={autoFocus}
            // ring-offset-0 overrides the site-wide *:focus-visible rule
            // (globals.css), which defaults every element to a 2px ring-offset
            // colored by the current background. Without it that offset reads as
            // a border-like gap around the box.
            className="w-full rounded-sm border border-border bg-input py-3 pl-10 pr-10 text-sm text-foreground placeholder-muted transition-colors focus:border-accent-600 focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-0"
          />

          {/* Loading indicator */}
          {isLoading && (
            <div className="absolute right-3">
              <Spinner size="sm" className="text-muted" />
            </div>
          )}

          {/* Close link — visible whenever the popup is open, regardless of
              whether anything's typed, matching the design (it closes the
              search UI; it does not clear the query — same as Escape below). */}
          {isOpen && !isLoading && (
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                inputRef.current?.blur();
              }}
              className="absolute right-3 flex items-center gap-1 text-xs text-muted hover:text-foreground"
              aria-label={tSearch("closeSearch")}
            >
              <span aria-hidden="true">✕</span> {t("close")}
            </button>
          )}
        </div>
      </form>

      {/* Suggestions Dropdown - Full Width. Portaled to #search-overlay-root (a
          header sibling of the dark row, see header.tsx) rather than
          document.body: that keeps it out of the dark-theme class while still
          nesting it under `<header>`, which is what lets the backdrop below
          sit above the header's own logo/konto/varukorg (compared locally,
          within the header's stacking context) as well as the page content
          behind it, leaving only the input (z-50 above) and this panel sharp. */}
      {isOpen && hasContent && createPortal(
        <>
          {/* Backdrop — covers the full viewport (header row included), not just
              the area below the input, so only the input and panel stay in focus. */}
          <div
            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />

          {/* Full-width dropdown, anchored under the input via measured top. */}
          <div
            ref={dropdownRef}
            className="fixed inset-x-0 z-50 border-b border-border bg-overlay shadow-2xl"
            style={{ top: dropdownTop }}
          >
            <div className="container mx-auto py-12">
              {/* Recent searches - the user's own device history, orthogonal to
                  the merchant-curated columns below, so it gets its own
                  full-width row above them rather than competing for a column. */}
              {showInitialState && recentSearches.length > 0 && (
                <div className="mb-6">
                  <div className="mb-3 flex items-center justify-between border-b-2 border-border pb-3">
                    <h3 className="text-[11px] font-mono uppercase tracking-[0.14em] text-muted">
                      {tSearch("recentSearches")}
                    </h3>
                    <button
                      onClick={clearAllRecentSearches}
                      className="text-xs text-muted hover:text-foreground transition-colors"
                    >
                      {tSearch("clearAll")}
                    </button>
                  </div>
                  <div className="grid gap-x-8 sm:grid-cols-2 lg:grid-cols-3">
                    {recentSearches.map((term) => (
                      <button
                        key={term}
                        onClick={() => handleRecentSearchClick(term)}
                        className="group flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-card-hover"
                      >
                        <svg
                          className="h-4 w-4 shrink-0 text-muted"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span className="flex-1 text-foreground">{term}</span>
                        <span
                          role="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            clearRecentSearch(term);
                          }}
                          className="rounded p-1 text-muted opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"
                        >
                          <svg
                            className="h-3.5 w-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Discovery row - popular terms, categories, brands - shown
                  before the user has typed anything worth debouncing on. */}
              {showInitialState && hasDiscoveryContent && (
                <div className="mb-6 grid gap-8 sm:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr] lg:gap-14">
                  {popularSearchTerms.length > 0 && (
                    <div>
                      <h3 className="mb-3 border-b-2 border-border pb-3 text-[11px] font-mono uppercase tracking-[0.14em] text-muted">
                        {tSearch("popularTerms")}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {popularSearchTerms.map((term) => (
                          <button
                            key={term.text}
                            type="button"
                            onClick={() => handlePopularTermClick(term)}
                            className="rounded-full border border-border px-5 py-[11px] text-[14.5px] font-medium text-foreground transition-colors hover:border-accent-600 hover:text-accent-foreground"
                          >
                            {term.text}
                          </button>
                        ))}
                      </div>

                      {/* Merchant contact fallback for "can't find it" — real
                          phone number (src/lib/company.ts), linked to the real
                          opening-hours page rather than a hardcoded hours string. */}
                      <div className="mt-6 rounded border border-border px-6 py-[22px]">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">
                          {tSearch("helpTitle")}
                        </p>
                        <SearchContactText messageKey="helpDescription" className="text-sm text-foreground" />
                        <StoreLink
                          href="/oppettider"
                          onClick={() => {
                            setIsOpen(false);
                            onSearch?.();
                          }}
                          className="mt-2 inline-block text-sm font-medium text-accent-foreground hover:underline"
                        >
                          {tSearch("helpOpeningHoursLink")} →
                        </StoreLink>
                      </div>
                    </div>
                  )}

                  {categories.length > 0 && (
                    <div>
                      <h3 className="mb-3 border-b-2 border-border pb-3 text-[11px] font-mono uppercase tracking-[0.14em] text-muted">
                        {tSearch("categories")}
                      </h3>
                      <ul>
                        {categories.slice(0, 6).map((category) => (
                          <li key={category.id}>
                            <StoreLink
                              href={`/${category.urlKey}`}
                              onClick={() => {
                                setIsOpen(false);
                                onSearch?.();
                              }}
                              className="flex items-center justify-between gap-2 border-b border-border/10 py-2 text-sm text-foreground transition-colors hover:text-primary-500"
                            >
                              <span className="truncate">{category.name}</span>
                              <span className="text-muted" aria-hidden="true">›</span>
                            </StoreLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {(brandMenu?.brands.length ?? 0) > 0 && (
                    <div>
                      <h3 className="mb-3 border-b-2 border-border pb-3 text-[11px] font-mono uppercase tracking-[0.14em] text-muted">
                        {tBrands("mainBrands")}
                      </h3>
                      <ul>
                        {brandMenu!.brands.map((brand) => (
                          <li key={brand.id}>
                            <StoreLink
                              href={`/${brand.urlKey}`}
                              onClick={() => {
                                setIsOpen(false);
                                onSearch?.();
                              }}
                              className="flex items-center justify-between gap-2 border-b border-border/10 py-2 text-sm text-foreground transition-colors hover:text-primary-500"
                            >
                              <span className="truncate">{brand.name}</span>
                              <span className="text-muted" aria-hidden="true">›</span>
                            </StoreLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* No Results State */}
              {noResults && (
                <div className="py-8 text-center">
                  <h3 className="mb-2 text-lg font-semibold text-heading">
                    {tSearch("noResults", { query: committedQuery })}
                  </h3>
                  <SearchContactText messageKey="noResultsCallUs" className="mx-auto max-w-lg text-sm text-muted" />
                </div>
              )}

              {/* Search Results */}
              {hasSuggestions && (
                <div>
                  {/* View All Results - top right, matching the design's placement */}
                  {committedQuery.trim() && (
                    <div className="mb-4 flex justify-end">
                      <button
                        onClick={() =>
                          handleSubmit({
                            preventDefault: () => {},
                          } as React.FormEvent)
                        }
                        // secondary, matching mobile-search's identical action:
                        // a soft CTA out of a suggestion list, not a page primary.
                        className={buttonClassName({ variant: 'primary' })}
                      >
                        {tSearch("viewAllResults", { query: committedQuery })}
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
                  {/* Left Column - Suggestions */}
                  <div className="space-y-4">
                    {/* Category Suggestions */}
                    {suggestedCategories.length > 0 && (
                      <div>
                        <h3 className="mb-3 border-b-2 border-border pb-3 text-[11px] font-mono uppercase tracking-[0.14em] text-muted">
                          {tSearch("categories")}
                        </h3>
                        <ul className="space-y-0.5">
                          {suggestedCategories.map((cat, index) => (
                            <li key={cat.urlKey}>
                              <button
                                type="button"
                                onMouseEnter={() => setSelectedIndex(index)}
                                onClick={() => {
                                  router.push(storePath(`/${cat.urlPath}`));
                                  setIsOpen(false);
                                  onSearch?.();
                                }}
                                className={cn(
                                  "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-card-hover",
                                  selectedIndex === index && "bg-card-hover",
                                )}
                              >
                                <svg
                                  className="h-4 w-4 shrink-0 text-muted"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 6h16M4 12h16M4 18h7"
                                  />
                                </svg>
                                <span className="text-foreground">
                                  <HighlightedText text={cat.name} query={committedQuery} />
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Brand Suggestions - matched server-side against all
                        brand categories, not just the curated main brands. */}
                    {brandMatches.length > 0 && (
                      <div>
                        <h3 className="mb-3 border-b-2 border-border pb-3 text-[11px] font-mono uppercase tracking-[0.14em] text-muted">
                          {tSearch("brands")}
                        </h3>
                        <ul className="space-y-0.5">
                          {brandMatches.map((brand) => (
                            <li key={brand.id}>
                              <StoreLink
                                href={`/${brand.urlKey}`}
                                onClick={() => {
                                  setIsOpen(false);
                                  onSearch?.();
                                }}
                                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-card-hover"
                              >
                                {/* Wrapped in one span, not passed straight into
                                    the flex row: HighlightedText can return several
                                    sibling nodes (split around the match), which
                                    the row's own `gap-2` would otherwise insert a
                                    gap between (e.g. "Aim" / "point" in Aimpoint). */}
                                <span>
                                  <HighlightedText text={brand.name} query={committedQuery} />
                                </span>
                              </StoreLink>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Products, as compact list rows rather than
                      cards: matches the mockup's row treatment (thumbnail +
                      name + article number + price) instead of a tile grid. */}
                  {suggestedProducts.length > 0 && (
                    <div>
                      <h3 className="mb-3 border-b-2 border-border pb-3 text-[11px] font-mono uppercase tracking-[0.14em] text-muted">
                        {tSearch("products")}
                      </h3>
                      <div className="grid gap-x-8 sm:grid-cols-2">
                        {suggestedProducts.map((product, index) => {
                          const itemIndex =
                            suggestedCategories.length + index;
                          return (
                            <StoreLink
                              key={product.sku}
                              href={`/${product.urlKey}`}
                              onClick={handleProductClick}
                              className={cn(
                                "grid grid-cols-[60px_1fr] items-center gap-3 rounded-lg p-2 transition-colors hover:bg-card-hover",
                                selectedIndex === itemIndex && "bg-card-hover",
                              )}
                            >
                              {/* Product Image */}
                              <div className="relative aspect-square overflow-hidden rounded-md bg-placeholder">
                                {product.thumbnail?.url && (
                                  <Image
                                    src={product.thumbnail.url}
                                    alt={
                                      product.thumbnail.label || product.name
                                    }
                                    fill
                                    sizes="60px"
                                    className="object-cover"
                                  />
                                )}
                              </div>
                              {/* Product Info */}
                              <div className="flex min-w-0 flex-col gap-0.5">
                                <p className="truncate text-sm font-semibold text-heading">
                                  <HighlightedText
                                    text={product.name}
                                    query={committedQuery}
                                  />
                                </p>
                                <p className="text-xs text-muted">
                                  {productBrands[product.sku] && (
                                    <>{productBrands[product.sku]} · </>
                                  )}
                                  {tProduct("sku")} {product.sku}
                                </p>
                                <PriceLabel
                                  price={product.price.final}
                                  originalPrice={
                                    product.price.discount &&
                                    product.price.discount.amount > 0 &&
                                    product.price.regular > product.price.final
                                      ? product.price.regular
                                      : undefined
                                  }
                                  currency={product.price.currency}
                                  size="sm"
                                />
                              </div>
                            </StoreLink>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                </div>
              )}
            </div>
          </div>
        </>,
        document.getElementById("search-overlay-root") ?? document.body,
      )}
    </div>
  );
}
